/* eslint-disable import/prefer-default-export */
/* eslint-disable camelcase */
import {
    ARCHITECTURE_1oo1,
    ARCHITECTURE_1oo2,
    ARCHITECTURE_1oo3,
    ARCHITECTURE_2oo2,
    ARCHITECTURE_2oo3,
} from "const"
import { SilArchitecture } from "client/utils"


interface Vdi_vde_2180_2019 {
    architecture: SilArchitecture
    ptc0A: number
    ptc1A: number
    ptc0B: number
    ptc1B: number
    ptc0C: number
    ptc1C: number
    beta: number
    t0: number
    t1: number
    t2: number
    duA: number
    duB: number
    duC: number
}

export const vdi_vde_2180_2019_PFD = ({
    architecture,
    ptc0A,
    ptc1A,
    ptc0B,
    ptc1B,
    ptc0C,
    ptc1C,
    beta,
    t0,
    t1,
    t2,
    duA,
    duB,
    duC,
}: Vdi_vde_2180_2019) => {
    const lambda0A = ptc0A * duA
    const lambda1A = (ptc1A - ptc0A) * duA
    const lambda2A = (1 - ptc1A) * duA

    const lambda0B = ptc0B * duB
    const lambda1B = (ptc1B - ptc0B) * duB
    const lambda2B = (1 - ptc1B) * duB

    const lambda0C = ptc0C * duC
    const lambda1C = (ptc1C - ptc0C) * duC
    const lambda2C = (1 - ptc1C) * duC

    if (architecture === ARCHITECTURE_1oo1) {
        return lambda0A * (t0 / 2) + lambda1A * (t1 / 2) + lambda2A * (t2 / 2)
    }

    if (architecture === ARCHITECTURE_1oo2) {
        const ptc0_avg = (ptc0A + ptc0B) / 2
        const ptc1_avg = (ptc1A + ptc1B) / 2

        const duMin = Math.min(duA, duB)
        const lambda0_avg = ptc0_avg * duMin
        const lambda1_avg = (ptc1_avg - ptc0_avg) * duMin
        const lambda2_avg = (1 - ptc1_avg) * duMin

        const pkA = (1 - beta) * (lambda0A * (t0 / 2) + lambda1A * (t1 / 2) + lambda2A * (t2 / 2))
        const pkB = (1 - beta) * (lambda0B * (t0 / 2) + lambda1B * (t1 / 2) + lambda2B * (t2 / 2))

        return (1 - beta) * (
            (pkA * lambda0B + pkB * lambda0A) * (t0 / 3)
            + (pkA * lambda1B + pkB * lambda1A) * (t1 / 3)
            + (pkA * lambda2B + pkB * lambda2A) * (t2 / 3)
        )
        + beta * ((t0 / 2) * lambda0_avg + (t1 / 2) * lambda1_avg + (t2 / 2) * lambda2_avg)
    }

    if (architecture === ARCHITECTURE_1oo3) {
        const ptc0_avg = (ptc0A + ptc0B + ptc0C) / 3
        const ptc1_avg = (ptc1A + ptc1B + ptc1C) / 3

        const duMin = Math.min(duA, duB, duC)
        const lambda0_avg = ptc0_avg * duMin
        const lambda1_avg = (ptc1_avg - ptc0_avg) * duMin
        const lambda2_avg = (1 - ptc1_avg) * duMin

        const betaRate = 1 - 0.5 * beta
        const pkA = betaRate * (lambda0A * (t0 / 2) + lambda1A * (t1 / 2) + lambda2A * (t2 / 2))
        const pkB = betaRate * (lambda0B * (t0 / 2) + lambda1B * (t1 / 2) + lambda2B * (t2 / 2))
        const pkC = betaRate * (lambda0C * (t0 / 2) + lambda1C * (t1 / 2) + lambda2C * (t2 / 2))

        const pkAB = betaRate * (
            (pkA * lambda0B + pkB * lambda0A) * (t0 / 3)
            + (pkA * lambda1B + pkB * lambda1A) * (t1 / 3)
            + (pkA * lambda2B + pkB * lambda2A) * (t2 / 3)
        )
        const pkBC = betaRate * (
            (pkB * lambda0C + pkC * lambda0B) * (t0 / 3)
            + (pkB * lambda1C + pkC * lambda1B) * (t1 / 3)
            + (pkB * lambda2C + pkC * lambda2B) * (t2 / 3)
        )
        const pkAC = betaRate * (
            (pkA * lambda0C + pkC * lambda0A) * (t0 / 3)
            + (pkA * lambda1C + pkC * lambda1A) * (t1 / 3)
            + (pkA * lambda2C + pkC * lambda2A) * (t2 / 3)
        )

        return betaRate * (
            (pkBC * lambda0A + pkAC * lambda0B + pkAB * lambda0C) * (t0 / 4)
            + (pkBC * lambda1A + pkAC * lambda1B + pkAB * lambda1C) * (t1 / 4)
            + (pkBC * lambda2A + pkAC * lambda2B + pkAB * lambda2C) * (t2 / 4)
        )
        + 0.5 * beta * ((t0 / 2) * lambda0_avg + (t1 / 2) * lambda1_avg + (t2 / 2) * lambda2_avg)
    }

    if (architecture === ARCHITECTURE_2oo2) {
        return (lambda0A * (t0 / 2) + lambda1A * (t1 / 2) + lambda2A * (t2 / 2))
            + (lambda0B * (t0 / 2) + lambda1B * (t1 / 2) + lambda2B * (t2 / 2))
    }

    if (architecture === ARCHITECTURE_2oo3) {
        const ptc0_avg = (ptc0A + ptc0B + ptc0C) / 3
        const ptc1_avg = (ptc1A + ptc1B + ptc1C) / 3

        const duMin = Math.min(duA, duB, duC)
        const lambda0_avg = ptc0_avg * duMin
        const lambda1_avg = (ptc1_avg - ptc0_avg) * duMin
        const lambda2_avg = (1 - ptc1_avg) * duMin

        const betaRate = 1 - (3 / 2) * beta
        const pkAB = betaRate * (
            (lambda0A * (t0 / 2) + lambda1A * (t1 / 2) + lambda2A * (t2 / 2))
            + (lambda0B * (t0 / 2) + lambda1B * (t1 / 2) + lambda2B * (t2 / 2))
        )
        const pkBC = betaRate * (
            (lambda0B * (t0 / 2) + lambda1B * (t1 / 2) + lambda2B * (t2 / 2))
            + (lambda0C * (t0 / 2) + lambda1C * (t1 / 2) + lambda2C * (t2 / 2))
        )
        const pkAC = betaRate * (
            (lambda0A * (t0 / 2) + lambda1A * (t1 / 2) + lambda2A * (t2 / 2))
            + (lambda0C * (t0 / 2) + lambda1C * (t1 / 2) + lambda2C * (t2 / 2))
        )

        return betaRate * (
            (pkBC * lambda0A + pkAC * lambda0B + pkAB * lambda0C) * (t0 / 3)
            + (pkBC * lambda1A + pkAC * lambda1B + pkAB * lambda1C) * (t1 / 3)
            + (pkBC * lambda2A + pkAC * lambda2B + pkAB * lambda2C) * (t2 / 3)
        )
        + (3 / 2) * beta * ((t0 / 2) * lambda0_avg + (t1 / 2) * lambda1_avg + (t2 / 2) * lambda2_avg)
    }

    return null
}
